/** Politique de jeux commune au build, au SDK et aux Functions. */
const scopes = new Set(['org-wide', 'owner-scoped']);
const objet = value => value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * LE FIL D'ECHANGES NE SE RESTREINT PAS A SON AUTEUR (decision du fondateur,
 * 11/10/2026) : un membre n'y verrait plus les conversations ni les messages
 * des autres, et Canaux comme le chatter des fiches deviendraient inutilisables.
 * Refuse partout ou une portee se regle ou se publie ; une publication d'avant
 * la regle se relit comme heritee (org-wide).
 */
export const FAMILLES_SANS_PORTEE_RESTREINTE = Object.freeze(['conversations', 'messages']);
export const REFUS_PORTEE_RESTREINTE = 'Les conversations et les messages ne se restreignent pas à leur auteur : Canaux en deviendrait inutilisable.';
export const porteeRestreinteInterdite = (family, scope) => scope === 'owner-scoped' && FAMILLES_SANS_PORTEE_RESTREINTE.includes(family);
/** Une declaration de champs sans portee interdite (le jeu herite) ; la meme si rien n'est a retirer. */
export function sansPorteeInterdite(champs) {
  if (!objet(champs) || !Array.isArray(champs.jeux) || !champs.jeux.some(j => objet(j) && porteeRestreinteInterdite(j.family, j.scope))) return champs;
  return { ...champs, jeux: champs.jeux.map(j => {
    if (!objet(j) || !porteeRestreinteInterdite(j.family, j.scope)) return j;
    const { scope: _retiree, ...jeu } = j;
    return jeu;
  }) };
}
export function validerPortees(value) {
  if (!objet(value)) throw new Error('Politique de portee invalide');
  for (const [family, types] of Object.entries(value)) {
    if (!/^[a-z][A-Za-z0-9]*$/.test(family) || ['constructor', 'prototype'].includes(family) || !objet(types)) throw new Error('Famille de portee invalide');
    for (const [type, scope] of Object.entries(types))
      if (!type || type.length > 80 || ['__proto__', 'prototype', 'constructor'].includes(type) || !scopes.has(scope)) throw new Error('Type ou portee invalide');
  }
  return value;
}
export function porteesDeJeux(champs, entities = []) {
  const resultat = {};
  const poser = (family, type, scope) => {
    validerPortees({ [family]: { [type]: scope } });
    if (!scopes.has(scope)) throw new Error(`Portee invalide : ${scope}`);
    if (porteeRestreinteInterdite(family, scope)) throw new Error(REFUS_PORTEE_RESTREINTE);
    resultat[family] ??= {};
    if (resultat[family][type] !== undefined && resultat[family][type] !== scope) throw new Error(`Portees contradictoires pour ${family}/${type}`);
    resultat[family][type] = scope;
  };
  for (const entity of entities) {
    for (const type of entity.types?.length ? entity.types : ['*']) {
      const jeu = champs?.jeux?.find(j => j.family === entity.name && (j.type ?? '*') === type);
      poser(entity.name, type, jeu?.scope ?? entity.scope ?? 'org-wide');
    }
  }
  for (const jeu of champs?.jeux ?? []) {
    if (jeu.family.startsWith('@a-decider')) continue;
    const entity = entities.find(e => e.name === jeu.family);
    poser(jeu.family, jeu.type ?? '*', jeu.scope ?? entity?.scope ?? 'org-wide');
  }
  return validerPortees(resultat);
}
export function fusionnerPortees(contributions) {
  const resultat = {};
  for (const contribution of contributions) for (const [family, types] of Object.entries(validerPortees(contribution))) {
    resultat[family] ??= {};
    for (const [type, scope] of Object.entries(types)) {
      if (resultat[family][type] !== undefined && resultat[family][type] !== scope) throw new Error(`Portees contradictoires pour ${family}/${type}`);
      resultat[family][type] = scope;
    }
  }
  return resultat;
}
export function politiqueEffective(modele, surcharge) {
  validerPortees(modele);
  if (surcharge === undefined || surcharge === null) return modele;
  if (!objet(surcharge) || surcharge.version !== 1 || !objet(surcharge.familles)) throw new Error('Projection de portee invalide');
  validerPortees(surcharge.familles);
  const resultat = structuredClone(modele);
  // Une projection d'avant la regle ne restreint plus le fil d'echanges.
  for (const [family, types] of Object.entries(surcharge.familles)) if (!FAMILLES_SANS_PORTEE_RESTREINTE.includes(family)) resultat[family] = { ...resultat[family], ...types };
  return resultat;
}
export function porteeDuJeu(politique, family, type) {
  const types = politique[family] ?? {};
  return (type === undefined ? undefined : types[type]) ?? types['*'] ?? 'org-wide';
}
export function auteurDeRequete(politique, family, type, uid, role, modele = politique) {
  if (role === 'owner' || role === 'admin') return null;
  const types = politique[family] ?? {};
  if (type === undefined && (Object.values(types).includes('owner-scoped') || Object.values(modele[family] ?? {}).includes('owner-scoped')) && types['*'] !== 'owner-scoped')
    throw new Error(`Le jeu ${family} exige un type pour appliquer sa portee`);
  if (porteeDuJeu(politique, family, type) !== 'owner-scoped') return null;
  if (!uid || uid === 'system') throw new Error('Une session est exigee par la portee');
  return uid;
}
